<template>
  <div class="d-flex template-picker" :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }">
    <div class="template-picker__list" v-show="!$vuetify.breakpoint.smAndDown || mobileStep === 'list'">
      <div class="template-picker__header">
        <v-text-field
          v-model="search"
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          :label="$t('components.template.picker.search')"
          class="ma-2"
        />
        <div class="d-flex flex-wrap align-center px-2 pb-1" style="gap: 6px">
          <template v-if="allowHtml && !isPlain">
            <v-chip
              small
              :color="formatFilter === MessageContentFormatEnum.HTML ? 'primary' : undefined"
              :outlined="formatFilter !== MessageContentFormatEnum.HTML"
              @click="formatFilter = MessageContentFormatEnum.HTML"
            >
              {{ $t("components.template.picker.formatHtml") }}
            </v-chip>
            <v-chip
              small
              :color="formatFilter === MessageContentFormatEnum.PLAIN ? 'primary' : undefined"
              :outlined="formatFilter !== MessageContentFormatEnum.PLAIN"
              @click="formatFilter = MessageContentFormatEnum.PLAIN"
            >
              {{ $t("components.template.picker.formatPlain") }}
            </v-chip>
          </template>
          <v-divider v-if="partnerId && allowHtml && !isPlain" vertical class="mx-1" style="height: 18px" />
          <v-chip
            v-if="partnerId"
            small
            :color="showSystemTemplates ? 'primary' : undefined"
            :outlined="!showSystemTemplates"
            @click="showSystemTemplates = !showSystemTemplates"
          >
            {{ $t("components.template.picker.systemTemplates") }}
          </v-chip>
          <v-chip
            small
            :color="onlyWithContext ? 'primary' : undefined"
            :outlined="!onlyWithContext"
            @click="onlyWithContext = !onlyWithContext"
          >
            {{ $t("components.template.picker.onlyWithContext") }}
          </v-chip>
        </div>
      </div>
      <div v-if="loading" class="d-flex justify-center align-center pa-4">
        <v-progress-circular indeterminate color="primary" size="28" />
      </div>
      <div v-else-if="!loading && filteredRows.length === 0" class="grey--text text-center pa-4 text-caption">
        {{ $t("components.template.picker.empty") }}
      </div>
      <v-list v-else dense class="overflow-y-auto template-picker__list-items">
        <v-list-item
          v-for="row of filteredRows"
          :key="row.key"
          :class="{ 'v-list-item--active': selectedId === row.key }"
          @click="onPreview(row)"
        >
          <v-list-item-content>
            <!-- Title + status icons (top right) -->
            <v-list-item-title class="d-flex align-center">
              <div class="template-picker__title d-flex align-baseline text-truncate">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <span class="text-truncate" v-bind="attrs" v-on="on">
                      {{ row.defaultTemplate.meta.title }}
                    </span>
                  </template>
                  <span>{{ row.defaultTemplate.meta.title }}</span>
                </v-tooltip>
                <span
                  v-if="row.key !== row.defaultTemplate.meta.title"
                  class="template-picker__key text-caption grey--text text--lighten-1 ml-2 flex-shrink-0"
                >
                  {{ row.key }}
                </span>
              </div>
              <v-spacer />
              <div class="d-flex align-center ml-2" style="gap: 6px">
                <v-tooltip v-if="row.defaultTemplate.isAdminTemplate" bottom>
                  <template #activator="{ on, attrs }">
                    <v-chip x-small outlined v-bind="attrs" v-on="on">
                      <v-icon x-small>mdi-robot-outline</v-icon>
                    </v-chip>
                  </template>
                  <span>{{ $t("components.template.picker.systemTemplates") }}</span>
                </v-tooltip>
                <v-tooltip v-if="missingContextFor(row.defaultTemplate).length" bottom>
                  <template #activator="{ on, attrs }">
                    <v-chip x-small outlined color="warning" v-bind="attrs" v-on="on">
                      <v-icon x-small>mdi-alert-circle-outline</v-icon>
                    </v-chip>
                  </template>
                  <span>{{
                    $t("components.template.picker.missingContextTooltip", {
                      categories: missingContextFor(row.defaultTemplate).join(", ")
                    })
                  }}</span>
                </v-tooltip>
                <v-tooltip v-if="!row.hasContextLanguage" bottom>
                  <template #activator="{ on, attrs }">
                    <v-chip x-small outlined color="warning" v-bind="attrs" v-on="on">
                      <v-icon x-small>mdi-translate</v-icon>
                    </v-chip>
                  </template>
                  <span>{{
                    $t("components.template.picker.missingLanguageTooltip", {
                      language: language.toUpperCase(),
                      fallback: row.defaultTemplate.meta.language.toUpperCase()
                    })
                  }}</span>
                </v-tooltip>
              </div>
            </v-list-item-title>

            <!-- Description -->
            <v-list-item-subtitle v-if="row.defaultTemplate.meta.description" class="text-truncate mt-1">
              {{ row.defaultTemplate.meta.description }}
            </v-list-item-subtitle>

            <!-- Category tags + Use action (bottom right) -->
            <div class="d-flex align-end mt-1">
              <div
                v-if="row.defaultTemplate.meta.categories && row.defaultTemplate.meta.categories.length"
                class="d-flex flex-wrap"
                style="gap: 4px"
              >
                <v-chip v-for="cat of row.defaultTemplate.meta.categories" :key="cat" x-small label outlined>
                  {{ cat }}
                </v-chip>
              </div>
              <v-spacer />
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    small
                    text
                    color="primary"
                    class="template-picker__use ml-2"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="useTemplate(row)"
                  >
                    {{ $t("components.template.picker.use") }}
                  </v-btn>
                </template>
                <span>{{ $t("components.template.picker.useTooltip") }}</span>
              </v-tooltip>
            </div>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </div>

    <div class="template-picker__preview pa-2" v-show="!$vuetify.breakpoint.smAndDown || mobileStep === 'preview'">
      <v-btn v-if="$vuetify.breakpoint.smAndDown" text small class="mb-1" @click="mobileStep = 'list'">
        <v-icon small left>mdi-chevron-left</v-icon>{{ $t("components.template.picker.back") }}
      </v-btn>
      <div class="d-flex align-center mb-1">
        <div class="text-caption grey--text">{{ $t("components.template.picker.preview") }}</div>
        <v-spacer />
        <v-btn v-if="previewedTemplate" text x-small color="primary" @click="goToTemplate(previewedTemplate)">
          <v-icon x-small left>mdi-open-in-new</v-icon>
          {{ $t("components.template.picker.goToTemplate") }}
        </v-btn>
        <v-btn text x-small color="primary" class="ml-1" @click="goToAllTemplates">
          <v-icon x-small left>mdi-open-in-new</v-icon>
          {{ $t("components.template.picker.goToAllTemplates") }}
        </v-btn>
      </div>
      <div v-if="!preview" class="grey--text text-center pa-8">{{ $t("components.template.picker.noSelection") }}</div>
      <template v-else>
        <div v-if="previewRow" class="d-flex flex-wrap align-center mb-2" style="gap: 6px">
          <span class="text-caption grey--text mr-1">{{ $t("components.template.picker.language") }}</span>
          <v-chip
            v-for="tpl of previewRow.templates"
            :key="tpl.meta.language"
            small
            :color="previewedTemplate && previewedTemplate.meta.language === tpl.meta.language ? 'primary' : undefined"
            :outlined="!previewedTemplate || previewedTemplate.meta.language !== tpl.meta.language"
            @click="selectPreviewLanguage(tpl)"
          >
            {{ tpl.meta.language | getFlagEmojiByLanguage }} {{ tpl.meta.language.toUpperCase() }}
          </v-chip>
        </div>
        <v-card outlined class="pa-3">
          <div class="font-weight-medium mb-2" v-text="preview.subject" />
          <v-divider class="mb-2" />
          <html-editor :value="preview.body" :readOnly="true" :outlined="false" :missingTokens="missingTokens" />
        </v-card>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import { MessageContentFormatEnum } from "@/lib/enum/templateEnums/messageContentFormat.enum";
import { ITemplateContext } from "@/lib/interfaces/template/templateContext.interface";
import { ITemplate } from "@/models/template.entity";
import { getFlagEmojiByLanguage } from "@/lib/CountryCodeHelper";
import { AdminTemplateModule } from "@/store/modules/admin-template.store";
import { PartnerTemplateModule } from "@/store/modules/partner-template.store";
import { TemplateTokenConfig } from "@/lib/configuration/template-token.configuration";
import { TokenCategoriesEnum } from "@/lib/enum/templateEnums/tokenCategories.enum";
import { AdminTemplateGoToHelper } from "@/lib/utility/admin-template.go-to-helper";
import { PartnerTemplateGoToHelper } from "@/lib/utility/partner-template.go-to-helper";
import HtmlEditor from "../utility/HtmlEditor.vue";

export interface ITemplatePickerRow {
  key: string;
  /** All language variants available for this key. */
  templates: ITemplate[];
  /** Variant shown by default: the context-language version if present, otherwise the first available. */
  defaultTemplate: ITemplate;
  /** Whether a variant exists in the requested context language. */
  hasContextLanguage: boolean;
}

/**
 * One row per unique template key. Each row carries every language variant of that key so the
 * preview can offer a language choice. `defaultTemplate` is the context-language variant when it
 * exists, otherwise the first available variant (and `hasContextLanguage` is false).
 */
export function groupTemplatesByKey(templates: ITemplate[], contextLanguage: LanguageCodeEnum): ITemplatePickerRow[] {
  const byKey = new Map<string, ITemplate[]>();
  for (const tpl of templates) {
    const list = byKey.get(tpl.key) ?? [];
    list.push(tpl);
    byKey.set(tpl.key, list);
  }
  const rows: ITemplatePickerRow[] = [];
  for (const [key, list] of byKey) {
    const contextMatch = list.find((t) => t.meta.language === contextLanguage);
    rows.push({
      key,
      templates: list,
      defaultTemplate: contextMatch ?? list[0],
      hasContextLanguage: !!contextMatch
    });
  }
  return rows;
}

@Component({ components: { HtmlEditor }, filters: { getFlagEmojiByLanguage } })
export default class TemplatePicker extends Vue {
  @Prop() partnerId?: string;
  @Prop() context!: ITemplateContext;
  @Prop({ required: true }) language!: LanguageCodeEnum;
  @Prop({ default: false }) isPlain!: boolean;
  @Prop({ default: true, type: Boolean }) allowHtml!: boolean;

  /** Exposes the enum to the template for the format-filter chips. */
  readonly MessageContentFormatEnum = MessageContentFormatEnum;

  search = "";
  formatFilter: MessageContentFormatEnum = MessageContentFormatEnum.HTML;
  selectedId: string | null = null;
  preview: { subject: string; body: string } | null = null;
  missingTokens: string[] = [];
  loading = false;
  showSystemTemplates = false;
  previewedTemplate: ITemplate | null = null;
  /** Row currently shown in the preview; drives the language chips. */
  previewRow: ITemplatePickerRow | null = null;
  onlyWithContext = false;
  mobileStep: "list" | "preview" = "list";

  created() {
    // Plain-text compose contexts (isPlain) or callers that disallow HTML must only see plain
    // templates — force the filter to PLAIN regardless of the (hidden) format toggle.
    if (!this.allowHtml || this.isPlain) this.formatFilter = MessageContentFormatEnum.PLAIN;
  }

  async mounted() {
    await this.fetchTemplates();
  }

  async fetchTemplates() {
    this.loading = true;
    try {
      const store = this.partnerId ? PartnerTemplateModule : AdminTemplateModule;
      store.setFilters([]);
      await store.fetchFirstPage(this.partnerId ? { partnerId: this.partnerId } : ({} as any));
    } catch (e) {
      this.$log?.error?.(e);
    } finally {
      this.loading = false;
    }
  }

  get templates(): ITemplate[] {
    return this.partnerId ? PartnerTemplateModule.filteredAndSorted : AdminTemplateModule.filteredAndSorted;
  }

  get visibleTemplates(): ITemplate[] {
    let list = this.templates;
    if (this.partnerId && !this.showSystemTemplates) {
      list = list.filter((t) => !t.isAdminTemplate);
    }
    list = list.filter((t) =>
      this.formatFilter === MessageContentFormatEnum.PLAIN
        ? t.meta.contentFormat === MessageContentFormatEnum.PLAIN
        : t.meta.contentFormat !== MessageContentFormatEnum.PLAIN
    );
    if (this.onlyWithContext) {
      list = list.filter((t) => this.missingContextFor(t).length === 0);
    }
    return list;
  }

  get rows(): ITemplatePickerRow[] {
    return groupTemplatesByKey(this.visibleTemplates, this.language);
  }

  get filteredRows(): ITemplatePickerRow[] {
    const q = this.search?.trim().toLowerCase();
    if (!q) return this.rows;
    return this.rows.filter((r) =>
      r.templates.some((t) => {
        // Short-circuit on the cheap fields first; only lowercase the (potentially large) body
        // when title and description don't already match, to keep typing responsive.
        const title = t.meta.title?.toLowerCase() ?? "";
        if (title.includes(q)) return true;
        const description = t.meta.description?.toLowerCase() ?? "";
        if (description.includes(q)) return true;
        return (t.content?.body?.toLowerCase() ?? "").includes(q);
      })
    );
  }

  onPreview(row: ITemplatePickerRow) {
    this.selectedId = row.key;
    this.previewRow = row;
    this.selectPreviewLanguage(row.defaultTemplate);
    if (this.$vuetify.breakpoint.smAndDown) {
      this.mobileStep = "preview";
    }
  }

  /** Switch the previewed language variant of the currently selected row. */
  selectPreviewLanguage(template: ITemplate) {
    this.previewedTemplate = template;
    this.renderPreview(template);
    this.$emit("preview", template);
  }

  useTemplate(row: ITemplatePickerRow) {
    // If this row is the one being previewed, honor the language the user picked in the preview;
    // otherwise fall back to the context-or-fallback variant.
    const selected =
      this.previewRow?.key === row.key && this.previewedTemplate ? this.previewedTemplate : row.defaultTemplate;
    this.$emit("select", selected);
  }

  /** Open the given template's detail page in a new tab. */
  goToTemplate(template: ITemplate) {
    if (this.partnerId) {
      new PartnerTemplateGoToHelper(this.$router).goToPartnerTemplateDetail({
        partnerId: this.partnerId,
        templateId: template.id,
        newTab: true
      });
    } else {
      new AdminTemplateGoToHelper(this.$router).goToAdminTemplateDetail({ templateId: template.id, newTab: true });
    }
  }

  /** Open the template overview in a new tab. */
  goToAllTemplates() {
    if (this.partnerId) {
      new PartnerTemplateGoToHelper(this.$router).goToPartnerTemplateTable({ partnerId: this.partnerId, newTab: true });
    } else {
      new AdminTemplateGoToHelper(this.$router).goToAdminTemplateTable({ newTab: true });
    }
  }

  missingContextFor(template: ITemplate): string[] {
    const tokens = template.content?.tokens ?? [];
    const categories = [
      ...new Set(
        tokens
          .map((t) => t.split(".")[0])
          .filter((c) => TemplateTokenConfig.objectCategories.includes(c as TokenCategoriesEnum))
      )
    ];
    return categories.filter((c) => !(this.context as Record<string, unknown>)?.[c]);
  }

  renderPreview(template: ITemplate) {
    // Show the raw template so placeholders like {{report.firstname}} stay visible in the preview,
    // but wrap them so the editor renders them as highlighted chips instead of plain text.
    this.preview = {
      subject: template.content.subject,
      body: this.highlightPlaceholders(template.content.body)
    };
    this.missingTokens = [];
  }

  /**
   * Wrap Mustache tokens `{{token}}` in placeholder-token spans so HtmlEditor's PlaceholderToken
   * node renders them as highlighted chips in the read-only preview.
   */
  highlightPlaceholders(html: string): string {
    return (html || "").replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_m, token) => {
      const t = String(token).trim();
      const escaped = t.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      return `<span data-placeholder-token="${escaped}">{{${escaped}}}</span>`;
    });
  }

  @Watch("allowHtml")
  onAllowHtmlChange(val: boolean) {
    if (!val) this.formatFilter = MessageContentFormatEnum.PLAIN;
  }
}
</script>

<style scoped>
.template-picker__key {
  font-size: 0.7rem;
  font-weight: 400;
}
.template-picker__title {
  flex: 1 1 auto;
  min-width: 0;
}
/* Use button is hidden by default; revealed on row hover, keyboard focus, or when the row is
   selected. pointer-events: none while hidden keeps it from being a clickable/tab-stop blank area. */
.template-picker__use {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.1s ease-in-out;
}
.template-picker__list-items .v-list-item:hover .template-picker__use,
.template-picker__list-items .v-list-item:focus-within .template-picker__use,
.template-picker__list-items .v-list-item.v-list-item--active .template-picker__use {
  opacity: 1;
  pointer-events: auto;
}
/* No hover on touch and tapping a row switches to the preview pane, so always show Use on mobile. */
.template-picker.flex-column .template-picker__use {
  opacity: 1;
  pointer-events: auto;
}
.template-picker {
  height: 100%;
  min-height: 360px;
  /* Keep panes inside the dialog; each pane scrolls on its own instead of the whole view. */
  overflow: hidden;
}
.template-picker__list {
  flex: 0 0 44%;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  /* min-height: 0 lets the inner list shrink and scroll within a flex parent. */
  min-height: 0;
  max-width: 650px;
}
/* Header (search + chips) stays a fixed-height block at the top;
   without this the Vuetify v-text-field (flex:1 1 auto) would stretch and push the list down. */
.template-picker__header {
  flex: 0 0 auto;
}
.template-picker__list-items {
  flex: 1 1 0;
  overflow-y: auto;
  min-height: 0;
}
.template-picker__preview {
  flex: 1 1 0;
  overflow-y: auto;
  min-height: 0;
}

/* On mobile, one pane fills the entire height at a time (v-show hides the other). */
.template-picker.flex-column .template-picker__list,
.template-picker.flex-column .template-picker__preview {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}
.template-picker.flex-column .template-picker__list {
  border-right: none;
  border-bottom: none;
}
</style>
